import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Raffle } from '@sorare/core/src/atoms/icons/Raffle';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge/index.tsx';
import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { WidgetButton } from '@sorare/core/src/atoms/ui/WidgetButton/index.tsx';
import { ANY_SPORT_SERIES_RAFFLE } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser/index.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';

import type { RaffleWidget_raffle } from './__generated__/index.graphql.ts';

const Circle = styled(Horizontal).attrs({ center: true })`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  background: var(--c-black);
  border-radius: 50%;
`;

type Props = {
  raffle: RaffleWidget_raffle;
};
export const RaffleWidget = ({ raffle }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { endDate, myRaffleTasks } = raffle;
  const { currentUser } = useCurrentUserContext();
  const raffleTickets = currentUser?.raffleTickets?.at(0)?.amount ?? 0;

  const showNewChip = raffleTickets === 0;
  const completedTasksCount = myRaffleTasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  ).length;

  return (
    <WidgetButton
      to={generateSportContextPath(ANY_SPORT_SERIES_RAFFLE)}
      chip={
        showNewChip ? (
          <WidgetChip state="new" />
        ) : (
          <WidgetChip state="timeLeft">
            <TimeLeft time={endDate} />
          </WidgetChip>
        )
      }
    >
      <Badge badgeContent={completedTasksCount} overlap="circular">
        <Circle>
          <Raffle />
        </Circle>
      </Badge>
    </WidgetButton>
  );
};

RaffleWidget.fragments = {
  raffle: gql`
    fragment RaffleWidget_raffle on Raffle {
      slug
      endDate
      myRaffleTasks {
        id
        aasmState
      }
    }
  ` as TypedDocumentNode<RaffleWidget_raffle>,
};
